import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import * as XLSX from "xlsx";

import {
  FaCalendarAlt,
  FaChartBar,
  FaChevronRight,
  FaFileExcel,
  FaSyncAlt,
  FaUserGraduate,
  FaCheck,
  FaTimes,
} from "react-icons/fa";

import Swal from "sweetalert2";

import api from "../../api";
import "./StudentAttendanceReportPage.css";


/* ============================================================
   MONTHS
============================================================ */

const MONTHS = [
  {
    number: 1,
    short: "JAN",
    name: "January",
  },
  {
    number: 2,
    short: "FEB",
    name: "February",
  },
  {
    number: 3,
    short: "MAR",
    name: "March",
  },
  {
    number: 4,
    short: "APR",
    name: "April",
  },
  {
    number: 5,
    short: "MAY",
    name: "May",
  },
  {
    number: 6,
    short: "JUN",
    name: "June",
  },
  {
    number: 7,
    short: "JUL",
    name: "July",
  },
  {
    number: 8,
    short: "AUG",
    name: "August",
  },
  {
    number: 9,
    short: "SEP",
    name: "September",
  },
  {
    number: 10,
    short: "OCT",
    name: "October",
  },
  {
    number: 11,
    short: "NOV",
    name: "November",
  },
  {
    number: 12,
    short: "DEC",
    name: "December",
  },
];


/* ============================================================
   NUMBER HELPER
============================================================ */

const toNumber = (value) => {

  const n =
    Number(value);

  return Number.isFinite(n)
    ? n
    : 0;
};


/* ============================================================
   PERCENTAGE
============================================================ */

const getPercentage = (
  present,
  total
) => {

  const p =
    toNumber(present);

  const t =
    toNumber(total);

  if (
    t <= 0
  ) {
    return 0;
  }

  return (
    (p / t) *
    100
  );
};


/* ============================================================
   ACADEMIC YEAR LABEL
============================================================ */

const getAcademicYearLabel = (
  item
) => {

  if (!item) {
    return "";
  }

  return (
    item.session_name ||
    item.name ||
    item.academic_year ||
    item.academic_year_name ||
    item.year_name ||
    item.title ||
    (
      item.start_year &&
      item.end_year
        ? `${item.start_year}-${item.end_year}`
        : ""
    ) ||
    (
      item.from_year &&
      item.to_year
        ? `${item.from_year}-${item.to_year}`
        : ""
    ) ||
    ""
  );
};


/* ============================================================
   ACADEMIC YEAR ID
============================================================ */

const getAcademicYearId = (
  item
) => {

  if (!item) {
    return "";
  }

  return (
    item.id ??
    item.academic_year_id ??
    item.academicYearId ??
    ""
  );
};


/* ============================================================
   GET LOCAL ACADEMIC YEAR ID
============================================================ */

const getLocalAcademicYearId = () => {

  const keys = [
    "academic_year_id",
    "academicYearId",
    "selected_academic_year_id",
    "selectedAcademicYearId",
    "current_academic_year_id",
    "currentAcademicYearId",
    "selected_year_id",
    "selectedYearId",
  ];

  for (
    const key of keys
  ) {

    const value =
      localStorage.getItem(
        key
      );

    if (
      value !== null &&
      String(value).trim() !== ""
    ) {

      return String(
        value
      ).trim();

    }

  }


  const objectKeys = [
    "academic_year",
    "academicYear",
    "selected_academic_year",
    "selectedAcademicYear",
    "current_academic_year",
    "currentAcademicYear",
  ];


  for (
    const key of objectKeys
  ) {

    const value =
      localStorage.getItem(
        key
      );

    if (!value) {
      continue;
    }


    try {

      const parsed =
        JSON.parse(
          value
        );

      const id =
        parsed?.id ??
        parsed?.academic_year_id ??
        parsed?.academicYearId;

      if (
        id !== undefined &&
        id !== null &&
        String(id).trim() !== ""
      ) {

        return String(
          id
        ).trim();

      }

    } catch (error) {

      // Ignore invalid JSON.

    }

  }

  return "";
};


/* ============================================================
   GET CURRENT YEAR
============================================================ */

const getCurrentYear = () => {

  return new Date()
    .getFullYear();

};


/* ============================================================
   MONTHS
============================================================ */

const normalizeMonth = (
  row,
  fallbackMonth
) => {

  const monthNumber =
    toNumber(
      row?.month_number ??
      row?.month ??
      fallbackMonth
    );


  const monthFromStatic =
    MONTHS.find(
      (item) =>
        item.number ===
        monthNumber
    );


  const monthName =
    row?.month_name ||
    row?.monthName ||
    (
      typeof row?.month ===
        "string"
        ? row.month
        : ""
    ) ||
    monthFromStatic?.name ||
    `Month ${monthNumber}`;


  const short =
    (
      monthFromStatic?.short ||
      String(
        monthName
      )
        .slice(0, 3)
        .toUpperCase()
    );


  const present =
    toNumber(
      row?.present
    );


  const absent =
    toNumber(
      row?.absent
    );


  const leave =
    toNumber(
      row?.leave ??
      row?.leave_count
    );


  const holiday =
    toNumber(
      row?.holiday
    );


  let total =
    toNumber(
      row?.total ??
      row?.total_attendance
    );


  /*
   * Backend normally returns COUNT(a.id)
   * as total.
   *
   * This fallback protects against
   * missing total.
   */

  if (
    total <= 0
  ) {

    total =
      present +
      absent +
      leave +
      holiday;

  }


  return {

    monthNumber,

    monthName,

    short,

    present,

    absent,

    leave,

    holiday,

    total,

    presentPercentage:
      getPercentage(
        present,
        total
      ),

  };

};


/* ============================================================
   BUILD 12 MONTHS
============================================================ */

const buildMonths = (
  responseMonths
) => {

  const source =
    Array.isArray(
      responseMonths
    )
      ? responseMonths
      : [];


  const map =
    new Map();


  source.forEach(
    (row, index) => {

      const normalized =
        normalizeMonth(
          row,
          index + 1
        );

      map.set(
        normalized.monthNumber,
        normalized
      );

    }
  );


  return MONTHS.map(
    (month) => {

      if (
        map.has(
          month.number
        )
      ) {

        return map.get(
          month.number
        );

      }


      return {

        monthNumber:
          month.number,

        monthName:
          month.name,

        short:
          month.short,

        present: 0,

        absent: 0,

        leave: 0,

        holiday: 0,

        total: 0,

        presentPercentage: 0,

      };

    }
  );

};


/* ============================================================
   GET TOTAL
============================================================ */

const normalizeTotal = (
  responseTotal,
  months
) => {

  if (
    responseTotal &&
    typeof responseTotal ===
      "object"
  ) {

    const present =
      toNumber(
        responseTotal.present
      );

    const absent =
      toNumber(
        responseTotal.absent
      );

    const leave =
      toNumber(
        responseTotal.leave ??
        responseTotal.leave_count
      );

    const holiday =
      toNumber(
        responseTotal.holiday
      );

    let total =
      toNumber(
        responseTotal.total ??
        responseTotal.total_attendance
      );


    if (
      total <= 0
    ) {

      total =
        present +
        absent +
        leave +
        holiday;

    }


    return {

      present,

      absent,

      leave,

      holiday,

      total,

      presentPercentage:
        getPercentage(
          present,
          total
        ),

    };

  }


  /*
   * Fallback:
   * calculate from months.
   */

  return months.reduce(
    (
      acc,
      row
    ) => {

      acc.present +=
        toNumber(
          row.present
        );

      acc.absent +=
        toNumber(
          row.absent
        );

      acc.leave +=
        toNumber(
          row.leave
        );

      acc.holiday +=
        toNumber(
          row.holiday
        );

      acc.total +=
        toNumber(
          row.total
        );

      return acc;

    },
    {
      present: 0,
      absent: 0,
      leave: 0,
      holiday: 0,
      total: 0,
      presentPercentage: 0,
    }
  );

};


/* ============================================================
   ACADEMIC YEAR LIST NORMALIZER
============================================================ */

const extractAcademicYears = (
  response
) => {

  const data =
    response?.data;


  const candidates = [

    data?.data,

    data?.academic_years,

    data?.years,

    data?.rows,

    Array.isArray(
      data
    )
      ? data
      : null,

  ];


  for (
    const candidate of
    candidates
  ) {

    if (
      Array.isArray(
        candidate
      )
    ) {

      return candidate;

    }

  }


  return [];

};


/* ============================================================
   GET PREFERRED ACADEMIC YEAR
============================================================ */

const findPreferredAcademicYear = (
  years
) => {

  if (
    !Array.isArray(years) ||
    years.length === 0
  ) {
    return null;
  }


  const active =
    years.find(
      (item) => {
        const status =
          String(item?.status || "")
            .toLowerCase()
            .trim();

        return (
          status === "active" ||
          item?.is_active === 1 ||
          item?.is_active === true ||
          item?.active === 1 ||
          item?.status === 1 ||
          item?.current === 1 ||
          item?.current === true
        );
      }
    );


  if (active) {
    return active;
  }


  const localId =
    getLocalAcademicYearId();

  if (localId) {

    const exact =
      years.find(
        (item) =>
          String(
            getAcademicYearId(
              item
            )
          ) ===
          String(localId)
      );

    if (exact) {
      return exact;
    }

  }


  return years[0];

};


/* ============================================================
   MAIN COMPONENT
============================================================ */

const StudentAttendanceReportPage =
  () => {

    /* --------------------------------------------------------
       STATE
    -------------------------------------------------------- */

    const [
      academicYears,
      setAcademicYears
    ] =
      useState([]);


    const [
      selectedAcademicYear,
      setSelectedAcademicYear
    ] =
      useState(null);


    const [
      months,
      setMonths
    ] =
      useState(
        MONTHS.map(
          (month) => ({
            monthNumber:
              month.number,
            monthName:
              month.name,
            short:
              month.short,
            present: 0,
            absent: 0,
            leave: 0,
            holiday: 0,
            total: 0,
            presentPercentage: 0,
          })
        )
      );


    const [
      total,
      setTotal
    ] =
      useState({
        present: 0,
        absent: 0,
        leave: 0,
        holiday: 0,
        total: 0,
        presentPercentage: 0,
      });


    const [
      loadingYears,
      setLoadingYears
    ] =
      useState(false);


    const [
      loadingReport,
      setLoadingReport
    ] =
      useState(false);


    const [
      errorMessage,
      setErrorMessage
    ] =
      useState("");


    const [
      lastLoadedAt,
      setLastLoadedAt
    ] =
      useState(null);


    /* --------------------------------------------------------
       CURRENT REPORT YEAR
       Calendar year comes from selected academic year name.
    -------------------------------------------------------- */

    const reportYear =
      useMemo(
        () => {

          const label =
            getAcademicYearLabel(
              selectedAcademicYear
            );


          const match =
            String(
              label
            ).match(
              /(\d{4})/
            );


          if (
            match &&
            match[1]
          ) {

            return Number(
              match[1]
            );

          }


          return getCurrentYear();

        },
        [
          selectedAcademicYear
        ]
      );


    /* --------------------------------------------------------
       SELECTED ACADEMIC YEAR ID
    -------------------------------------------------------- */

    const academicYearId =
      useMemo(
        () =>
          getAcademicYearId(
            selectedAcademicYear
          ),
        [
          selectedAcademicYear
        ]
      );


    /* ========================================================
       LOAD ACADEMIC YEARS
    ======================================================== */

    const loadAcademicYears =
      useCallback(
        async () => {

          setLoadingYears(
            true
          );


          try {

            const response =
              await api.get(
                "/school/academic_year/list"
              );


            const years =
              extractAcademicYears(
                response
              );


            setAcademicYears(
              years
            );


            const preferred =
              findPreferredAcademicYear(
                years
              );


            if (
              preferred
            ) {

              setSelectedAcademicYear(
                preferred
              );

              const preferredId =
                getAcademicYearId(
                  preferred
                );

              if (preferredId) {
                localStorage.setItem(
                  "academic_year_id",
                  String(preferredId)
                );
              }

            } else {

              /*
               * Fallback object when API
               * is unavailable but localStorage
               * has academic_year_id.
               */

              const localId =
                getLocalAcademicYearId();


              if (
                localId
              ) {

                setSelectedAcademicYear(
                  {
                    id: localId,
                    name:
                      localStorage.getItem(
                        "academic_year_name"
                      ) ||
                      "",
                  }
                );

              }

            }

          } catch (
            error
          ) {

            console.error(
              "Academic year load error:",
              error
            );


            /*
             * Don't block report if localStorage
             * already contains academic year ID.
             */

            const localId =
              getLocalAcademicYearId();


            if (
              localId
            ) {

              setSelectedAcademicYear(
                {
                  id: localId,
                  name:
                    localStorage.getItem(
                      "academic_year_name"
                    ) ||
                    "",
                }
              );

            }

          } finally {

            setLoadingYears(
              false
            );

          }

        },
        []
      );


    /* ========================================================
       LOAD REPORT
    ======================================================== */

    const loadReport =
      useCallback(
        async (
          showError = true
        ) => {

          const id =
            getAcademicYearId(
              selectedAcademicYear
            );


          if (
            !id
          ) {

            setErrorMessage(
              "Academic year is not available."
            );

            return;

          }


          setLoadingReport(
            true
          );


          if (
            showError
          ) {

            setErrorMessage(
              ""
            );

          }


          try {

            const response =
              await api.get(
                "/student-attendance/report",
                {
                  params: {
                    year: reportYear,
                    academic_year_id: id,
                  },
                }
              );


            const payload =
              response?.data || {};


            console.log(
              "STUDENT ATTENDANCE REPORT RESPONSE:",
              payload
            );


            /*
             * Current backend response:
             *
             * {
             *   status: true,
             *   message: "...",
             *   data: {
             *      year,
             *      months,
             *      total
             *   }
             * }
             */

            let reportData =
              payload?.data;


            /*
             * Also support direct response.
             */

            if (
              !reportData ||
              typeof reportData !==
                "object"
            ) {

              reportData =
                payload;

            }


            if (
              payload?.status === false &&
              payload?.success === false
            ) {

              throw new Error(
                payload?.message ||
                "Unable to load attendance report."
              );

            }


            const normalizedMonths =
              buildMonths(
                reportData?.months
              );


            const normalizedTotal =
              normalizeTotal(
                reportData?.total,
                normalizedMonths
              );


            setMonths(
              normalizedMonths
            );


            setTotal(
              normalizedTotal
            );


            setLastLoadedAt(
              new Date()
            );


            setErrorMessage(
              ""
            );

          } catch (
            error
          ) {

            console.error(
              "Student attendance report error:",
              error
            );


            const serverMessage =
              error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              "Unable to load attendance report.";


            setErrorMessage(
              serverMessage
            );


            if (
              showError
            ) {

              Swal.fire(
                "Error",
                serverMessage,
                "error"
              );

            }

          } finally {

            setLoadingReport(
              false
            );

          }

        },
        [
          selectedAcademicYear,
          reportYear,
        ]
      );


    /* ========================================================
       INITIAL LOAD
    ======================================================== */

    useEffect(
      () => {

        loadAcademicYears();

      },
      [
        loadAcademicYears
      ]
    );


    /* ========================================================
       LOAD REPORT AFTER YEAR READY
    ======================================================== */

    useEffect(
      () => {

        if (
          academicYearId
        ) {

          loadReport(
            false
          );

        }

      },
      [
        academicYearId,
        loadReport
      ]
    );


    /* ========================================================
       REFRESH
    ======================================================== */

    const handleRefresh =
      () => {

        loadReport(
          true
        );

      };


    /* ========================================================
       EXCEL EXPORT
    ======================================================== */

    const exportExcel =
      () => {

        if (
          !months.length
        ) {

          return;

        }


        const rows =
          months.map(
            (
              row,
              index
            ) => ({

              "#":
                index + 1,

              Month:
                row.monthName,

              "Total Attendance":
                row.total,

              Present:
                row.present,

              Absent:
                row.absent,

              Leave:
                row.leave,

              Holiday:
                row.holiday,

              "Present %":
                `${row.presentPercentage.toFixed(2)}%`,

            })
          );


        rows.push({

          "#":
            "",

          Month:
            "TOTAL",

          "Total Attendance":
            total.total,

          Present:
            total.present,

          Absent:
            total.absent,

          Leave:
            total.leave,

          Holiday:
            total.holiday,

          "Present %":
            `${total.presentPercentage.toFixed(2)}%`,

        });


        const worksheet =
          XLSX.utils.json_to_sheet(
            rows
          );


        worksheet["!cols"] = [
          {
            wch: 7,
          },
          {
            wch: 18,
          },
          {
            wch: 20,
          },
          {
            wch: 14,
          },
          {
            wch: 14,
          },
          {
            wch: 14,
          },
          {
            wch: 14,
          },
          {
            wch: 14,
          },
        ];


        const workbook =
          XLSX.utils.book_new();


        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          "Attendance"
        );


        XLSX.writeFile(
          workbook,
          `Student_Attendance_${reportYear}.xlsx`
        );

      };


    /* ========================================================
       MONTH CLICK
       --------------------------------------------------------
       IMPORTANT:
       Current backend has no month->class endpoint.
       So don't call a fake endpoint.
    ======================================================== */

    const handleMonthClick =
      (
        row
      ) => {

        Swal.fire({

          icon:
            "info",

          title:
            `${row.monthName} Attendance`,

          html:
            `
              <div style="text-align:left;line-height:1.8">
                <div><strong>Total:</strong> ${row.total}</div>
                <div><strong>Present:</strong> ${row.present}</div>
                <div><strong>Absent:</strong> ${row.absent}</div>
                <div><strong>Leave:</strong> ${row.leave}</div>
                <div><strong>Holiday:</strong> ${row.holiday}</div>
                <div><strong>Present %:</strong> ${row.presentPercentage.toFixed(2)}%</div>
              </div>
            `,

          confirmButtonText:
            "OK",

        });

      };


    /* ========================================================
       RENDER
    ======================================================== */

    return (

      <div className="student-attendance-report-page">


        {/* ==================================================
           HEADER
        ================================================== */}

        <div className="sar-page-header">

          <div className="sar-page-title-wrap">

            <div className="sar-page-icon">

              <FaUserGraduate />

            </div>


            <div>

              <h1 className="sar-page-title">

                Student Attendance Report

              </h1>


              <p className="sar-page-subtitle">

                Daily and monthly student attendance summary

              </p>

            </div>

          </div>


          <div className="sar-header-actions">

            <button
              type="button"
              className="sar-btn sar-btn-excel"
              onClick={
                exportExcel
              }
              disabled={
                loadingReport ||
                !months.length
              }
            >

              <FaFileExcel />

              Excel

            </button>


            <button
              type="button"
              className="sar-btn sar-btn-refresh"
              onClick={
                handleRefresh
              }
              disabled={
                loadingReport
              }
            >

              <FaSyncAlt
                className={
                  loadingReport
                    ? "sar-spin"
                    : ""
                }
              />

              Refresh

            </button>

          </div>

        </div>


        {/* ==================================================
           ERROR
        ================================================== */}

        {errorMessage && (

          <div className="sar-error">

            {errorMessage}

          </div>

        )}


        {/* ==================================================
           SUMMARY CARDS
        ================================================== */}

        <div className="sar-summary-grid">


          {/* TOTAL */}

          <div className="sar-summary-card">

            <div className="sar-summary-icon total">

              <FaUserGraduate />

            </div>


            <div>

              <div className="sar-summary-label">

                TOTAL ATTENDANCE

              </div>


              <div className="sar-summary-value">

                {loadingReport
                  ? "..."
                  : total.total}

              </div>

            </div>

          </div>


          {/* PRESENT */}

          <div className="sar-summary-card">

            <div className="sar-summary-icon present">

              <FaCheck />

            </div>


            <div>

              <div className="sar-summary-label">

                PRESENT

              </div>


              <div className="sar-summary-value present-text">

                {loadingReport
                  ? "..."
                  : total.present}

              </div>

            </div>

          </div>


          {/* ABSENT */}

          <div className="sar-summary-card">

            <div className="sar-summary-icon absent">

              <FaTimes />

            </div>


            <div>

              <div className="sar-summary-label">

                ABSENT

              </div>


              <div className="sar-summary-value absent-text">

                {loadingReport
                  ? "..."
                  : total.absent}

              </div>

            </div>

          </div>


          {/* LEAVE */}

          <div className="sar-summary-card">

            <div className="sar-summary-icon leave">

              <FaCalendarAlt />

            </div>


            <div>

              <div className="sar-summary-label">

                LEAVE

              </div>


              <div className="sar-summary-value leave-text">

                {loadingReport
                  ? "..."
                  : total.leave}

              </div>

            </div>

          </div>


        </div>


        {/* ==================================================
           ATTENDANCE OVERVIEW
        ================================================== */}

        <div className="sar-overview">

          <div>

            <div className="sar-overview-label">

              ATTENDANCE OVERVIEW

            </div>


            <div className="sar-overview-title">

              {total.total > 0
                ? "Attendance Data Available"
                : "No Attendance Data"}

            </div>


            <div className="sar-overview-sub">

              Present =
              {total.present}
              {" / "}
              Total =
              {total.total}

            </div>

          </div>


          <div className="sar-overview-number">

            {total.presentPercentage.toFixed(2)}%

          </div>

        </div>


        {/* ==================================================
           MONTH TABLE
        ================================================== */}

        <div className="sar-table-card">

          <div className="sar-table-header">

            <div>

              <h2 className="sar-table-title">

                Month Wise Attendance

              </h2>


              <p className="sar-table-subtitle">

                Click any month to view attendance summary.

              </p>

            </div>


            {lastLoadedAt && (

              <div className="sar-last-loaded">

                Updated{" "}
                {lastLoadedAt.toLocaleTimeString(
                  "en-IN"
                )}

              </div>

            )}

          </div>


          <div className="sar-table-scroll">

            <table className="sar-table">

              <thead>

                <tr>

                  <th>
                    MONTH
                  </th>

                  <th>
                    TOTAL ATTENDANCE
                  </th>

                  <th>
                    PRESENT
                  </th>

                  <th>
                    ABSENT
                  </th>

                  <th>
                    LEAVE
                  </th>

                  <th>
                    PRESENT %
                  </th>

                  <th>
                    ACTION
                  </th>

                </tr>

              </thead>


              <tbody>

                {loadingReport ? (

                  <tr>

                    <td
                      colSpan="7"
                      className="sar-loading-row"
                    >

                      <FaSyncAlt className="sar-spin" />

                      Loading attendance report...

                    </td>

                  </tr>

                ) : (

                  months.map(
                    (
                      row
                    ) => (

                      <tr
                        key={
                          row.monthNumber
                        }
                      >

                        {/* MONTH */}

                        <td>

                          <button
                            type="button"
                            className="sar-month-button"
                            onClick={() =>
                              handleMonthClick(
                                row
                              )
                            }
                          >

                            <span className="sar-month-badge">

                              {row.short}

                            </span>


                            <span className="sar-month-name">

                              {row.monthName}

                              <small>

                                {reportYear}

                              </small>

                            </span>

                          </button>

                        </td>


                        {/* TOTAL */}

                        <td className="sar-number">

                          {row.total}

                        </td>


                        {/* PRESENT */}

                        <td className="sar-number sar-present">

                          {row.present}

                        </td>


                        {/* ABSENT */}

                        <td className="sar-number sar-absent">

                          {row.absent}

                        </td>


                        {/* LEAVE */}

                        <td className="sar-number sar-leave">

                          {row.leave}

                        </td>


                        {/* PERCENT */}

                        <td className="sar-number sar-percent">

                          {row.presentPercentage.toFixed(
                            2
                          )}%

                        </td>


                        {/* ACTION */}

                        <td>

                          <button
                            type="button"
                            className="sar-view-button"
                            onClick={() =>
                              handleMonthClick(
                                row
                              )
                            }
                          >

                            View

                            <FaChevronRight />

                          </button>

                        </td>

                      </tr>

                    )
                  )

                )}


                {/* ==================================================
                   TOTAL ROW
                ================================================== */}

                {!loadingReport && (

                  <tr className="sar-total-row">

                    <td>

                      TOTAL

                    </td>


                    <td>

                      {total.total}

                    </td>


                    <td>

                      {total.present}

                    </td>


                    <td>

                      {total.absent}

                    </td>


                    <td>

                      {total.leave}

                    </td>


                    <td>

                      {total.presentPercentage.toFixed(
                        2
                      )}%

                    </td>


                    <td>

                      —

                    </td>

                  </tr>

                )}

              </tbody>

            </table>

          </div>

        </div>


        {/* ==================================================
           FOOTER SUMMARY
        ================================================== */}

        <div className="sar-footer-summary">

          <span>

            Total:
            <strong>

              {total.total}

            </strong>

          </span>


          <span className="sar-footer-present">

            Present:
            <strong>

              {total.present}

            </strong>

          </span>


          <span className="sar-footer-absent">

            Absent:
            <strong>

              {total.absent}

            </strong>

          </span>


          <span className="sar-footer-leave">

            Leave:
            <strong>

              {total.leave}

            </strong>

          </span>

        </div>


      </div>

    );

  };


export default StudentAttendanceReportPage;